<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { CallsStore } from '@/store/modules/Calls/CallsStore'

// Плашка «вернуться в звонок». Появляется, когда звонок идёт (или звонит), а мы
// ушли с экрана звонка (например, нажали «К чатам»). Клик — обратно в звонок.
const { callState } = CallsStore.get()
const route = useRoute()
const router = useRouter()

const onCallScreen = computed(
  () => route.name === 'CallActive' || route.name === 'CallOutgoing',
)
const activeCall = computed(() => callState.activeCall)
const outgoingCall = computed(() => callState.outgoingCall)
// Активный звонок за пределами экрана показывается богатым мини-окном (CallPip).
// Плашка остаётся только для ИСХОДЯЩЕГО (пока звонит) — у него нет медиа для PiP.
const show = computed(
  () => !onCallScreen.value && !activeCall.value && !!outgoingCall.value,
)

const label = computed(() => 'Исходящий звонок')

// Плашку можно перетащить: на телефоне она стоит поверх кнопок в шапке списка
// чатов, и до них было не добраться. Положение запоминаем, чтобы не двигать
// её заново при каждом звонке.
const STORAGE_KEY = 'callReturnBannerPos'
const bannerRef = ref<HTMLElement | null>(null)
const pos = ref<{ x: number; y: number } | null>(readSavedPos())
const dragging = ref(false)
let pointerId: number | null = null
let grab = { dx: 0, dy: 0 }
let moved = false

function readSavedPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed
  } catch {
    // Хранилище недоступно (приватный режим) — просто оставляем место по умолчанию.
  }

  return null
}

function savePos() {
  try {
    if (pos.value) localStorage.setItem(STORAGE_KEY, JSON.stringify(pos.value))
  } catch {
    // Не смогли запомнить — не беда, плашка всё равно на месте до перезагрузки.
  }
}

/** Не даём утащить плашку за край экрана — иначе её было бы не вернуть. */
function clampIntoScreen() {
  const el = bannerRef.value
  if (!el || !pos.value) return
  const { width, height } = el.getBoundingClientRect()
  const maxX = Math.max(0, window.innerWidth - width)
  const maxY = Math.max(0, window.innerHeight - height)
  pos.value = {
    x: Math.min(Math.max(pos.value.x, 0), maxX),
    y: Math.min(Math.max(pos.value.y, 0), maxY),
  }
}

function onPointerDown(e: PointerEvent) {
  const el = bannerRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  // С этого момента считаем координаты от левого верхнего угла плашки: место по
  // умолчанию задано центрированием, и смешивать его с перетаскиванием нельзя.
  pos.value = { x: rect.left, y: rect.top }
  grab = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
  moved = false
  dragging.value = true
  pointerId = e.pointerId
  el.setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
  if (!dragging.value || e.pointerId !== pointerId) return
  const next = { x: e.clientX - grab.dx, y: e.clientY - grab.dy }
  if (Math.abs(next.x - (pos.value?.x ?? 0)) > 2 || Math.abs(next.y - (pos.value?.y ?? 0)) > 2) {
    moved = true
  }
  pos.value = next
  clampIntoScreen()
}

function onPointerUp(e: PointerEvent) {
  if (!dragging.value || e.pointerId !== pointerId) return
  dragging.value = false
  pointerId = null
  bannerRef.value?.releasePointerCapture(e.pointerId)
  clampIntoScreen()
  savePos()
}

function onClick() {
  // Отпустили после перетаскивания — это не нажатие.
  if (moved) {
    moved = false

    return
  }
  router.push('/call/outgoing')
}

const style = computed(() =>
  pos.value
    ? { left: `${pos.value.x}px`, top: `${pos.value.y}px`, transform: 'none' }
    : undefined,
)

watch(show, (visible) => {
  if (visible) requestAnimationFrame(clampIntoScreen)
})

onMounted(() => window.addEventListener('resize', clampIntoScreen))
onBeforeUnmount(() => window.removeEventListener('resize', clampIntoScreen))
</script>

<template>
  <transition name="cvret">
    <button
      v-if="show"
      ref="bannerRef"
      class="cvret"
      :class="{ 'cvret--dragging': dragging }"
      :style="style"
      title="Можно перетащить"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @click="onClick"
    >
      <span class="cvret__pulse" />
      <span class="cvret__text">{{ label }}</span>
      <span class="cvret__cta">Вернуться</span>
    </button>
  </transition>
</template>

<style scoped>
.cvret {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border: none;
  border-radius: 100px;
  cursor: grab;
  /* Перетаскивание ведём сами: иначе на телефоне жест уходил в прокрутку. */
  touch-action: none;
  background: var(--color-primary, #2f6fed);
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  font-family: var(--font-family-text);
  font-weight: 600;
  font-size: 14px;
}
.cvret:hover { filter: brightness(1.05); }
.cvret--dragging { cursor: grabbing; opacity: 0.92; }
.cvret__pulse {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #46e08a;
  box-shadow: 0 0 0 0 rgba(70, 224, 138, 0.7);
  animation: cvretPulse 1.6s infinite;
}
@keyframes cvretPulse {
  0% { box-shadow: 0 0 0 0 rgba(70, 224, 138, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(70, 224, 138, 0); }
  100% { box-shadow: 0 0 0 0 rgba(70, 224, 138, 0); }
}
.cvret__cta {
  padding: 3px 10px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.22);
  font-weight: 700;
}
.cvret-enter-active,
.cvret-leave-active { transition: opacity 0.25s ease; }
.cvret-enter-from,
.cvret-leave-to { opacity: 0; }
/* На узком экране подпись короче — плашка перестаёт закрывать кнопки в шапке. */
@media screen and (max-width: 750px) {
  .cvret { padding: 8px 12px; font-size: 13px; gap: 8px; }
}
</style>
